import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Sparkles, Crown, Mic2, ScanText, BookOpen, Search, Code, Scale, Building2, Plug, Database } from "lucide-react";
import { useLocation, useNavigate } from "react-router";
import { useAuthContext } from "@/hooks/useAuthContext";
import { useGetAccountByIdQuery } from "@/redux/api/accounts.rtkq";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import { hasPlanConnector, WEBAPP_CONNECTOR } from "@/utils/appAccess";

interface NavItem {
  key: string;
  /** Nest includedApps id for plan filter; defaults to key. */
  appId?: string;
  label: string;
  to: string;
  icon: React.ReactNode;
}
const NAV_ITEMS: Array<NavItem> = [
  { key: "recordings", label: "הקלטת שיחות", to: "/recordings", icon: <Mic2 className="h-4 w-4" /> },
  { key: "ai-assistant", label: "סוכן משפטי", to: "/assistant", icon: <Sparkles className="h-4 w-4" /> },
  { key: "scan-documents-extraction", label: "מסמכים", to: "/documents", icon: <ScanText className="h-4 w-4" /> },
  { key: "word-addon", label: "תוסף word", to: "/knowledge", icon: <BookOpen className="h-4 w-4" /> },
  // Placeholder pages (no Nest app yet) — shown with webapp-connector; hidden when filtering by includedApps.
  { key: "search", label: "חיפוש מסמכים", to: "/search", icon: <Search className="h-4 w-4" /> },
  { key: "legal-assistant", label: "עוזר משפטי", to: "/legal-assistant", icon: <Scale className="h-4 w-4" /> },
];
const ActiveBall = () => {
  return (
    <div className="active-ball-background absolute -left-15 size-[100px] rounded-3xl rotate-45">
      <div className="active-ball m-4 absolute size-[20px] rounded-full bg-radial-[at_25%_25%] from-blue-300  to-indigo-500 to-115%" />
    </div>
  );
};

const NavigationMenuItems = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setOpenMobile, open } = useSidebar();
  const { isUserSuperAdmin, isAccountAdmin } = useAuthContext();
  const auth = useAuth();

  const accountId = auth.getAccountId();

  const { data: account, isSuccess, isLoading } = useGetAccountByIdQuery({ accountId: accountId! }, { skip: !accountId });

  // Show loading skeleton while waiting for accountId or account data
  if (!accountId || isLoading || !isSuccess) {
    return (
      <>
        <SidebarMenuItem>
          <SidebarMenuButton disabled>
            <Skeleton className="h-6 w-6 bg-primary" />
            <Skeleton className="h-6 w-20 rounded bg-primary" />
          </SidebarMenuButton>
        </SidebarMenuItem>
        <SidebarMenuItem>
          <SidebarMenuButton disabled>
            <Skeleton className="h-6 w-6 bg-primary" />
            <Skeleton className="h-6 w-20 rounded bg-primary" />
          </SidebarMenuButton>
        </SidebarMenuItem>
      </>
    );
  }

  // With webapp-connector, show the full product catalog (teasers on zero credits).
  // Without the connector, keep the previous includedApps filter.
  const showAllProductNav = hasPlanConnector(account, WEBAPP_CONNECTOR);
  const includedAppsIds = account?.subscription.plan.includedApps.map((app) => app.appIdentifier);
  const filteredNavItems = showAllProductNav
    ? NAV_ITEMS
    : NAV_ITEMS.filter((item) => includedAppsIds?.includes(item.appId ?? item.key));

  // Legalix Connect: entitlement-gated — shown only when the office's plan
  // carries a partner-integration feature (or for super-admins), so
  // non-entitled offices see zero UI change.
  const hasIntegrations =
    account?.subscription.plan.features?.some((f) => f.startsWith("integration-")) ||
    isUserSuperAdmin();

  const userNavItems = [
    ...filteredNavItems,
    ...(hasIntegrations
      ? [{ key: "integrations", label: "אינטגרציות", to: "/integrations", icon: <Plug className="h-4 w-4" /> }]
      : []),
    ...(isAccountAdmin() ? [{ key: "office", label: "נהל משרד", to: "/office/dashboard", icon: <Building2 className="h-4 w-4" /> }] : []),
    ...(isUserSuperAdmin()
      ? [
          {
            key: "super-admin",
            label: "ניהול מערכת",
            to: "/super-admin",
            icon: <Crown className="h-4 w-4" />,
          },
          {
            key: "court-scraper",
            label: "נט המשפט (סריקה ומסמכים)",
            to: "/court-scraper",
            icon: <Database className="h-4 w-4" />,
          },
        ]
      : []),
    ...(import.meta.env.VITE_NODE_ENV === "development"
      ? [{ key: "developer", label: "R&D", to: "/developer", icon: <Code className="h-4 w-4" /> }]
      : []),
  ];

  return userNavItems.map((item) => {
    const isActive = location.pathname.startsWith(item.to);
    return (
      <SidebarMenuItem key={item.key}>
        <SidebarMenuButton
          isActive={isActive}
          onClick={() => {
            setOpenMobile(false);
            navigate(item.to);
          }}
        >
          {item.icon}
          {item.label}
          {isActive && open && <ActiveBall />}
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  });
};

const NavigationSection = () => {
  return (
    <SidebarGroup>
      <SidebarGroupLabel>אפליקציות</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          <NavigationMenuItems />
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
};

export default NavigationSection;
